import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { ESTIMATES_ROUTES } from "@routes/estimates"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    ESTIMATES_LIST_CONTROLS_ACTIVE_TAB,
    ESTIMATES_LIST_CONTROLS_KEYWORD_SEARCH,
    ESTIMATES_LIST_CONTROLS_PHASE,
    ESTIMATES_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { ESTIMATE_CARDS_VIEW_ATTRIBUTE_OPTIONS, ESTIMATE_CARDS_VIEW_SORT_OPTIONS } from "./EstimateList.constants"
import { EstimateObjectsViewTabs } from "./EstimateObjectsViewTabs"
import EstimateCardsView from "./views/EstimateCardsView/EstimateCardsView"
import EstimateTableView from "./views/EstimateTableView/EstimateTableView"

export default function EstimateList() {
    const { hasPermission } = useHasAccess()
    const isDesktop = useIsDesktop()

    const [createRoute] = useSlugExtractor([ESTIMATES_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="estimate-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={ESTIMATES_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle label={objectMap.estimate.labelPlural} />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            estimates_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                estimates_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Estimates" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={ESTIMATES_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={ESTIMATES_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={ESTIMATES_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Estimates"
                            objectType="estimate"
                            objectCreateRoute={createRoute}
                            objectIndexName="Estimate"
                            objectName="Estimate"
                            defaultActivePhase="open"
                            isRowClickable={hasPermission("estimates_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "estimates_create_permission",
                                PERMISSION_LEVEL.RESTRICTED,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={ESTIMATE_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={ESTIMATE_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="open" title="Open" />
                                                <ObjectsView.Phase phase="closed" title="Closed" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <EstimateObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <EstimateTableView />}
                                    {activeViewType === "cards" && <EstimateCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
